import { TicketOut } from '../api/types';

export const generateBetItemId = (eventId: string, marketId: string, outcomeId: string, specifiers?: Record<string, string> | string) => {
  let specStr = '';
  if (typeof specifiers === 'string') {
    specStr = specifiers;
  } else if (specifiers) {
    specStr = Object.entries(specifiers)
      .map(([k, v]) => `${k}=${v}`)
      .join('|');
  }
  return `${eventId}-${marketId}-${outcomeId}${specStr ? '-' + specStr : ''}`;
};

export const isTicketWon = (t: TicketOut): boolean => {
  if (!t.legs || t.legs.length === 0) return false;
  return t.legs.every(l => l.result_status === 'won' || l.result_status === 'win');
};

export const isTicketLost = (t: TicketOut): boolean => {
  if (!t.legs || t.legs.length === 0) return false;
  return t.legs.some(l => l.result_status === 'lost' || l.result_status === 'lose');
};

export const isOpenForCashout = (t: TicketOut): boolean => {
  if (t.status !== 'open' && t.status !== 'accepted') return false;
  if (!t.legs || t.legs.length === 0) return true;
  if (isTicketLost(t)) return false;
  if (isTicketWon(t)) return false;
  // Also check if all legs are resulted in some way (won, void, cancelled)
  const allResulted = t.legs.every(l => ['won', 'win', 'void', 'cancelled'].includes(l.result_status || ''));
  if (allResulted) return false;
  return true;
};

export const buildEventGroups = (bets: { eventId?: string; marketId?: string; outcomeId?: string; specifiers?: string; isCustomBet?: boolean; customBetSelections?: { market_id: string; specifiers: string; outcome_id: string }[]; odds?: string }[]) => {
  // Separate CustomBet (MK+) items from normal items
  const customBetItems = bets.filter(b => b.isCustomBet && b.customBetSelections && b.customBetSelections.length > 0);
  const normalItems = bets.filter(b => !b.isCustomBet);

  const groupedByEvent = normalItems.reduce((acc, b) => {
    if (!b.eventId || !b.marketId || !b.outcomeId) return acc;
    if (!acc[b.eventId]) acc[b.eventId] = [];
    acc[b.eventId].push({
      market_id: b.marketId,
      outcome_id: b.outcomeId,
      ...(b.specifiers ? { specifiers: b.specifiers } : {}),
    });
    return acc;
  }, {} as Record<string, { market_id: string; outcome_id: string; specifiers?: string }[]>);

  // Per API docs section 6.2: Use operator "any" for all event groups in the
  // regular betting slip. "any" means each selection is an alternative for that
  // event — the backend generates one accumulator path per alternative
  // (multi-way). operator "all" (CustomBet) should only be used through a
  // dedicated Bet Builder panel that verifies provider compatibility via
  // /v2/brands/{brand_code}/custombet/events/{event_id}/filter first.
  const event_groups = Object.entries(groupedByEvent).map(([event_id, selections]) => {
    return { event_id, operator: 'any' as const, selections };
  });

  // CustomBet items → operator: 'all', pricing: 'provider_custom_bet'
  customBetItems.forEach(cb => {
    if (!cb.eventId || !cb.customBetSelections) return;
    // Only send the request fields — display labels stay in the slip.
    const selections = cb.customBetSelections.map(s => ({
      market_id: s.market_id,
      specifiers: s.specifiers,
      outcome_id: s.outcome_id,
    }));
    // Send accepted_odds only when we hold a valid price. An unknown price must
    // be omitted so the backend prices at the current provider quote instead of
    // rejecting on a stale/empty value.
    const parsed = parseFloat((cb.odds ?? '').replace(',', '.'));
    const accepted_odds = !isNaN(parsed) && parsed > 1 ? parsed.toFixed(6) : undefined;
    event_groups.push({
      event_id: cb.eventId,
      operator: 'all' as const,
      pricing: 'provider_custom_bet',
      selections,
      ...(accepted_odds ? { accepted_odds } : {}),
    } as any);
  });

  return event_groups;
};
